// Authentication session of the advisor workstation.
// Contract consumed from @horizon/api-client:
//   supabase           : the configured SupabaseClient singleton
//   chargerProfil(id)  : Promise<Profil> for the authenticated user
import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
import type { Session } from '@supabase/supabase-js';
import type { Profil, RoleUtilisateur } from '@horizon/types';
import { chargerProfil, supabase } from '@horizon/api-client';
import { messageErreur } from '@/utils/erreurs';
import { demarrerVeilleInactivite } from '@/utils/inactivite';

/** Imposed by the corpus: an idle workstation is signed out after 30 minutes. */
export const DELAI_INACTIVITE_MS = 30 * 60 * 1000;

/** Roles allowed to mutate data. Conformité and direction are read-only. */
const ROLES_ECRITURE: readonly RoleUtilisateur[] = ['conseiller', 'administrateur'];

export const useSessionStore = defineStore('session', () => {
  const session = shallowRef<Session | null>(null);
  const profil = ref<Profil | null>(null);
  const chargement = ref(false);
  const erreur = ref<string | null>(null);

  /** Identifier of the pending TOTP challenge, when a second factor is required. */
  const facteurId = ref<string | null>(null);
  const defiId = ref<string | null>(null);

  let initialisation: Promise<void> | null = null;
  let arreterVeille: (() => void) | null = null;

  const role = computed<RoleUtilisateur | null>(() => profil.value?.role ?? null);
  const defiTotpEnCours = computed(() => defiId.value !== null);
  const estAuthentifie = computed(
    () => session.value !== null && profil.value !== null && !defiTotpEnCours.value,
  );
  const peutEcrire = computed(() => role.value !== null && ROLES_ECRITURE.includes(role.value));
  const estConformite = computed(() => role.value === 'conformite');
  const estDirection = computed(() => role.value === 'direction');
  const organisationId = computed(() => profil.value?.organisation_id ?? null);
  const nomComplet = computed(() =>
    profil.value === null ? '' : `${profil.value.prenom} ${profil.value.nom}`,
  );
  const initiales = computed(() => {
    if (profil.value === null) {
      return '';
    }
    return `${profil.value.prenom.charAt(0)}${profil.value.nom.charAt(0)}`.toUpperCase();
  });

  /** Load the profile attached to the current user, or clear it when signed out. */
  async function rafraichirProfil(): Promise<void> {
    const utilisateur = session.value?.user ?? null;
    if (utilisateur === null) {
      profil.value = null;
      return;
    }
    profil.value = await chargerProfil(utilisateur.id);
  }

  /** Detect whether Supabase requires a second factor before granting aal2. */
  async function evaluerSecondFacteur(): Promise<boolean> {
    const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (error !== null || data === null) {
      return false;
    }
    if (data.nextLevel !== 'aal2' || data.nextLevel === data.currentLevel) {
      return false;
    }
    const facteurs = await supabase.auth.mfa.listFactors();
    const totp = facteurs.data?.totp?.[0] ?? null;
    if (totp === null) {
      return false;
    }
    const defi = await supabase.auth.mfa.challenge({ factorId: totp.id });
    if (defi.error !== null || defi.data === null) {
      throw defi.error ?? new Error('Le second facteur est indisponible.');
    }
    facteurId.value = totp.id;
    defiId.value = defi.data.id;
    return true;
  }

  function reinitialiserDefi(): void {
    facteurId.value = null;
    defiId.value = null;
  }

  /** Restore an existing session and subscribe to Supabase auth changes. Idempotent. */
  function initialiser(): Promise<void> {
    if (initialisation !== null) {
      return initialisation;
    }
    initialisation = (async () => {
      const { data } = await supabase.auth.getSession();
      session.value = data.session;
      if (data.session !== null) {
        const secondFacteur = await evaluerSecondFacteur();
        if (!secondFacteur) {
          await rafraichirProfil();
          activerVeille();
        }
      }
      supabase.auth.onAuthStateChange((evenement, nouvelleSession) => {
        session.value = nouvelleSession;
        if (evenement === 'SIGNED_OUT' || nouvelleSession === null) {
          profil.value = null;
          reinitialiserDefi();
          desactiverVeille();
        }
      });
    })();
    return initialisation;
  }

  function activerVeille(): void {
    if (arreterVeille !== null) {
      return;
    }
    arreterVeille = demarrerVeilleInactivite(DELAI_INACTIVITE_MS, () => {
      void deconnecter('inactivite');
    });
  }

  function desactiverVeille(): void {
    if (arreterVeille === null) {
      return;
    }
    arreterVeille();
    arreterVeille = null;
  }

  /** Email and password sign-in. Resolves to true when a TOTP challenge is pending. */
  async function connecter(email: string, motDePasse: string): Promise<boolean> {
    chargement.value = true;
    erreur.value = null;
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password: motDePasse,
      });
      if (error !== null) {
        throw error;
      }
      session.value = data.session;
      const secondFacteur = await evaluerSecondFacteur();
      if (secondFacteur) {
        return true;
      }
      await rafraichirProfil();
      activerVeille();
      return false;
    } catch (cause) {
      erreur.value = messageErreur(cause, 'Identifiants incorrects ou compte désactivé.');
      throw cause;
    } finally {
      chargement.value = false;
    }
  }

  /** Answer the pending TOTP challenge with the six digit code. */
  async function verifierTotp(code: string): Promise<void> {
    if (facteurId.value === null || defiId.value === null) {
      throw new Error("Aucun défi d'authentification en cours.");
    }
    chargement.value = true;
    erreur.value = null;
    try {
      const { data, error } = await supabase.auth.mfa.verify({
        factorId: facteurId.value,
        challengeId: defiId.value,
        code,
      });
      if (error !== null) {
        throw error;
      }
      reinitialiserDefi();
      session.value = data === null ? session.value : { ...data };
      await rafraichirProfil();
      activerVeille();
    } catch (cause) {
      erreur.value = messageErreur(cause, 'Code à usage unique invalide ou expiré.');
      throw cause;
    } finally {
      chargement.value = false;
    }
  }

  /** Sign out, then send the browser back to the login screen. */
  async function deconnecter(motif: 'utilisateur' | 'inactivite' = 'utilisateur'): Promise<void> {
    desactiverVeille();
    reinitialiserDefi();
    await supabase.auth.signOut();
    session.value = null;
    profil.value = null;
    const { default: router } = await import('@/router');
    await router.push({
      name: 'connexion',
      query: motif === 'inactivite' ? { motif: 'inactivite' } : {},
    });
  }

  return {
    session,
    profil,
    chargement,
    erreur,
    role,
    defiTotpEnCours,
    estAuthentifie,
    peutEcrire,
    estConformite,
    estDirection,
    organisationId,
    nomComplet,
    initiales,
    initialiser,
    connecter,
    verifierTotp,
    deconnecter,
  };
});
